import { useInputType } from '@/hooks/useInputType';
import { FOCUS_KEY_ATTRIBUTE, focusNearestVisibleContent } from '@/utils';
import { useFocusable, UseFocusableConfig } from '@noriginmedia/norigin-spatial-navigation';
import { useEffect, useState } from 'react';

export type ExtendFocusableConfig<P> = UseFocusableConfig<P> & {
  /**
   * FocusContext 를 제공하는 목록 컨테이너인지.
   *
   * 컨테이너는 그 자체가 포커스 대상이 되어서는 안 된다. 자식이 아직 로드되지 않은 동안에는
   * norigin 이 컨테이너 자신에게 포커스를 주고, 크기만 큰 빈 영역이라 화면에서 포커스가 사라진다.
   * (극장판처럼 줄 목록 → 줄별 카드 순으로 두 번 로드하는 화면에서 특히 잘 발생한다)
   *
   * 이 플래그를 켜면
   *   1) 진입/이동 대상 후보에서 제외되고 (data-focus-key 를 붙이지 않는다)
   *   2) 그래도 포커스가 들어온 경우 방향키 한 번으로 화면에 보이는 카드로 빠져나온다
   */
  isContainer?: boolean;
};

export const useExtendFocusable = <P,>(props?: ExtendFocusableConfig<P>) => {
  const { inputType } = useInputType();
  const [prevInputType, setPrevInputType] = useState(inputType);
  const [localFocused, setLocalFocused] = useState(false);
  const { isContainer = false, ...focusableProps } = props ?? {};
  const { ref, focused, focusSelf, focusKey, ...args } = useFocusable({
    onFocus: () => setLocalFocused(true),
    onBlur: () => setLocalFocused(false),
    // 컨테이너에 포커스가 남는 것은 비정상 상태다. 방향키로 정상 위치로 복구시킨다.
    ...(isContainer ? { onArrowPress: () => focusNearestVisibleContent() } : {}),
    ...focusableProps,
  });
  const [hovered, setHovered] = useState(focused);

  // DOM 노드에서 focusKey 를 역추적할 수 있게 표시해 둔다.
  // (메뉴에서 콘텐츠로 진입할 때 화면에 보이는 항목을 찾는 데 사용한다. findNearestVisibleFocusKey 참고)
  // 표시가 붙어 있다는 것은 "지금 실제로 포커스를 받아도 되는 대상"을 뜻한다.
  const isFocusTarget = !isContainer && props?.focusable !== false;
  useEffect(() => {
    const current = ref.current;

    if (!current || !current.setAttribute) {
      return;
    }

    if (isFocusTarget) {
      current.setAttribute(FOCUS_KEY_ATTRIBUTE, focusKey);
    } else {
      current.removeAttribute(FOCUS_KEY_ATTRIBUTE);
    }
  }, [focusKey, isFocusTarget, ref]);

  useEffect(() => {
    const onMouseEnter = () => {
      setHovered(true);
    };
    const onMouseLeave = () => {
      setHovered(false);
    };

    const current = ref.current;
    if (current) {
      current.addEventListener('mouseenter', onMouseEnter);
      current.addEventListener('mouseleave', onMouseLeave);
    }

    return () => {
      if (current) {
        current.removeEventListener('mouseenter', onMouseEnter);
        current.removeEventListener('mouseleave', onMouseLeave);
      }
    };
  }, [ref, inputType, setHovered]);

  useEffect(() => {
    if (!focused) {
      setPrevInputType(inputType);
      return;
    }

    const current = ref.current;

    // 마우스로 전환된 순간에만 DOM 포커스를 해제한다. (hover 스타일과 :focus 스타일이 겹치는 것을 방지)
    if (prevInputType !== inputType && inputType === 'MOUSE') {
      setPrevInputType(inputType);

      if (current && current.blur) {
        current.blur();
      }
      setLocalFocused(false);
      return;
    }

    setPrevInputType(inputType);
    setLocalFocused(true);

    // 포커스 링은 CSS `:focus`로만 그려지기 때문에, DOM 포커스를 잃으면 링이 사라진다.
    // (예: 터치로 조작한 뒤 리모컨으로 전환) norigin 포커스를 기준으로 DOM 포커스를 복구한다.
    if (inputType !== 'MOUSE' && current && current.focus && document.activeElement !== current) {
      current.focus({ preventScroll: true });
    }
  }, [focused, inputType, prevInputType, ref]);

  return { ref, focusSelf, focusKey, focused: inputType === 'MOUSE' ? hovered : localFocused, ...args };
};
